import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminRequestTypes = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    libelle: '',
    statusId: ''
  });

  // Récupérer les types de demande
  const { data: requestTypesData, isLoading: loadingTypes } = useQuery(
    'request-types',
    async () => {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/request-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    }
  );

  // Récupérer les statuts pour request_types
  const { data: statusesData } = useQuery(
    'statuses-request-types',
    async () => {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/parameters/status?tableRattachement=request_types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    }
  );

  // Mutation pour créer
  const createMutation = useMutation(
    async (data) => {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${API_URL}/request-types`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Type de demande créé avec succès');
        queryClient.invalidateQueries('request-types');
        closeModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la création');
      }
    }
  );

  // Mutation pour mettre à jour
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(`${API_URL}/request-types/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Type de demande modifié avec succès');
        queryClient.invalidateQueries('request-types');
        closeModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la modification');
      }
    }
  );

  // Mutation pour supprimer
  const deleteMutation = useMutation(
    async (id) => {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/request-types/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    {
      onSuccess: () => {
        toast.success('Type de demande supprimé avec succès');
        queryClient.invalidateQueries('request-types');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
      }
    }
  );

  const openCreateModal = () => {
    setEditingType(null);
    setFormData({ libelle: '', statusId: '' });
    setShowModal(true);
  };

  const openEditModal = (type) => {
    setEditingType(type);
    setFormData({
      libelle: type.libelle,
      statusId: type.statusId
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingType(null);
    setFormData({ libelle: '', statusId: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.libelle || !formData.statusId) {
      toast.error('Tous les champs sont requis');
      return;
    }

    if (editingType) {
      updateMutation.mutate({ id: editingType.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (type) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${type.libelle}" ?`)) {
      deleteMutation.mutate(type.id);
    }
  };

  if (loadingTypes) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-900 mb-2">
              Types de Demande
            </h1>
            <p className="text-gray-600">
              Gérez les différents types de demandes disponibles dans le système
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nouveau type
          </button>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Libellé
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Utilisation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requestTypesData?.map((type) => (
              <tr key={type.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-gray-900">
                    {type.libelle}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`badge ${type.status?.isActive ? 'badge-success' : 'badge-gray'}`}>
                    {type.status?.libelle}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {type._count?.requests || 0} demande(s)
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => openEditModal(type)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Modifier"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(type)}
                      className="text-red-600 hover:text-red-900"
                      title="Supprimer"
                      disabled={type._count?.requests > 0}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={closeModal} />

            <div className="inline-block bg-white rounded-lg shadow-xl transform transition-all sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {editingType ? 'Modifier le type de demande' : 'Nouveau type de demande'}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="form-label">
                        Libellé <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.libelle}
                        onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                        className="form-input"
                        placeholder="Ex: Résiliation, Suspension..."
                        required
                      />
                    </div>

                    <div>
                      <label className="form-label">
                        Statut <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.statusId}
                        onChange={(e) => setFormData({ ...formData, statusId: e.target.value })}
                        className="form-input"
                        required
                      >
                        <option value="">Sélectionner un statut</option>
                        {statusesData?.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.libelle}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="btn-primary sm:ml-3"
                  >
                    {(createMutation.isLoading || updateMutation.isLoading) && (
                      <LoadingSpinner size="small" className="mr-2" />
                    )}
                    {editingType ? 'Modifier' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary mt-3 sm:mt-0"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRequestTypes;
